// BalanceContainer.tsx — Customs declaration / settlement view
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMediaQuery, useTheme } from '@mui/material';
import { useTravel } from '../../Contexts/TravelContext';
import { formatNumber } from '../../Contexts/CurrencyContext';
import SelfExpenseDialog from './Dialogs/SelfExpenseDialog';
import ConfirmationDialog from '../ConfirmationDialog';
import { insertExpense } from '../../Api/Api';
import { auth } from '../../Api/FirebaseConfig';
import { useMessage } from '../../Contexts/NotifContext';
import { Perf, Stamp } from '../Design/Atoms';

interface PendingSettlement {
  fromId: number;
  toId: number;
  amount: number;
  fromName: string;
  toName: string;
}

const BalanceContainer: React.FC = () => {
  const travelCtx = useTravel();
  const { setPayload } = useMessage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const userBalances = travelCtx.state.balances || [];
  const [selfExpenseDialog, setSelfExpenseDialog] = useState(false);
  const [pending, setPending] = useState<PendingSettlement | null>(null);
  const [settling, setSettling] = useState(false);

  const total = travelCtx.state.indiBalance?.total ?? 0;
  const positive = total >= 0;

  const getUserName = (userId: number) => {
    const u = travelCtx.state.users.find((user) => user.userId === userId);
    return u ? u.userName : 'Unknown';
  };

  // The logged-in member's own position — the answer users actually want,
  // framed as "you owe / you're owed" instead of a generic transfer list.
  const myId = travelCtx.state.users.find(
    (u) => u.email === auth.currentUser?.email,
  )?.userId;
  const iOwe = userBalances.filter((b) => b.from === myId); // I pay these
  const owedToMe = userBalances.filter((b) => b.to === myId); // these pay me
  const iOweTotal = iOwe.reduce((s, b) => s + b.amount, 0);
  const owedToMeTotal = owedToMe.reduce((s, b) => s + b.amount, 0);
  const myNet = owedToMeTotal - iOweTotal;

  const recordSettlement = async () => {
    if (!pending || settling) return;
    const tripId = travelCtx.state.chosenTrip?.tripIdShared;
    if (!tripId) return;
    setSettling(true);
    try {
      await insertExpense({
        tripId,
        date: new Date().toISOString().slice(0, 10),
        description: `Settlement: ${pending.fromName} → ${pending.toName}`,
        amount: pending.amount,
        paidBy: pending.fromId,
        splitbw: [{ userId: pending.toId, amount: pending.amount }],
        selfExpense: false,
      });
      setPayload({ type: 'success', message: 'Settlement logged. Balance cleared.' });
      travelCtx.state.refreshData?.();
      setPending(null);
    } catch {
      setPayload({ type: 'error', message: 'Could not record settlement.' });
    } finally {
      setSettling(false);
    }
  };

  return (
    <motion.div
      key="balance"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="ts-paper"
      style={{ padding: 0, position: 'relative', overflow: 'hidden' }}
    >
      {/* Heading band */}
      <div
        style={{
          padding: '20px 24px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div className="ts-eyebrow">Folio III</div>
          <h3
            className="ts-display"
            style={{
              margin: '6px 0 0',
              fontSize: 28,
              fontVariationSettings: '"SOFT" 30, "opsz" 144',
            }}
          >
            Customs declaration.
          </h3>
          <div
            className="ts-mono"
            style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-faded)', letterSpacing: '0.18em' }}
          >
            Settlement plan · minimum transactions
          </div>
        </div>

        <Stamp
          text={positive ? 'Credit' : 'Debit'}
          date="·"
          tone={positive ? 'ledger' : 'stamp'}
          size={84}
          rotate={6}
        />
      </div>

      {/* Your position — current member's net, in plain terms */}
      {myId !== undefined && (
        <div
          style={{
            background: myNet >= 0 ? 'var(--ledger-soft)' : 'var(--paper-deep)',
            borderTop: '1px solid var(--rule-soft)',
            padding: '16px 24px',
          }}
        >
          <div className="ts-label">Your position</div>
          <div
            className="ts-display"
            style={{ fontSize: 24, margin: '4px 0 2px', lineHeight: 1.1 }}
          >
            {Math.abs(myNet) < 0.01
              ? "You're all settled up"
              : myNet > 0
                ? `You're owed ${formatNumber(owedToMeTotal - iOweTotal)}`
                : `You owe ${formatNumber(iOweTotal - owedToMeTotal)}`}
          </div>
          {(iOwe.length > 0 || owedToMe.length > 0) && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {iOwe.map((b, i) => (
                <div key={`o${i}`} className="ts-mono" style={{ fontSize: 13 }}>
                  <span style={{ color: 'var(--stamp)' }}>You owe</span>{' '}
                  {getUserName(b.to)} · {formatNumber(b.amount)}
                </div>
              ))}
              {owedToMe.map((b, i) => (
                <div key={`d${i}`} className="ts-mono" style={{ fontSize: 13 }}>
                  <span style={{ color: 'var(--ledger)' }}>{getUserName(b.from)} owes you</span>{' '}
                  · {formatNumber(b.amount)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Total summary block */}
      <div
        style={{
          background: 'var(--paper-deep)',
          borderTop: '1px solid var(--rule-soft)',
          borderBottom: '1px solid var(--rule-soft)',
          padding: '18px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div className="ts-label">Total declared</div>
          <div
            className="ts-num"
            style={{
              fontSize: 30,
              fontWeight: 700,
              color: positive ? 'var(--ledger)' : 'var(--stamp)',
              marginTop: 4,
            }}
          >
            {positive ? '+' : '−'}₹{formatNumber(Math.abs(total))}
          </div>
        </div>

        <button
          className="ts-btn ts-btn-ink"
          onClick={() => setSelfExpenseDialog(true)}
        >
          Itemised contributions ↗
        </button>
      </div>

      {/* Transactions */}
      <div style={{ padding: '8px 0 12px' }}>
        {userBalances && userBalances.length > 0 ? (
          userBalances.map((balance: any, index: number) => {
            const sender = getUserName(balance.from);
            const receiver = getUserName(balance.to);
            return (
              <motion.div
                key={`${balance.from}-${balance.to}-${index}`}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.04 }}
                style={
                  isMobile
                    ? {
                        padding: '14px 18px',
                        borderBottom: '1px dashed var(--rule-soft)',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        gridTemplateAreas: '"from arrow to" "amount amount amount" "action action action"',
                        rowGap: 10,
                        columnGap: 10,
                        alignItems: 'center',
                      }
                    : {
                        padding: '14px 24px',
                        borderBottom: '1px dashed var(--rule-soft)',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        gap: 18,
                        alignItems: 'center',
                      }
                }
              >
                {/* Debtor */}
                <div style={isMobile ? { gridArea: 'from', minWidth: 0 } : undefined}>
                  <div className="ts-label">From · debtor</div>
                  <div
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: isMobile ? 17 : 22,
                      fontWeight: 500,
                      marginTop: 2,
                      color: 'var(--ink)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {sender}
                  </div>
                </div>

                {/* Centre arrow on mobile (between debtor/creditor) */}
                {isMobile && (
                  <div style={{ gridArea: 'arrow', textAlign: 'center', color: 'var(--ink-faded)', fontSize: 18 }}>
                    →
                  </div>
                )}

                {/* Amount + settle action */}
                <div
                  style={
                    isMobile
                      ? { gridArea: 'amount', textAlign: 'center' }
                      : { textAlign: 'center', minWidth: 200 }
                  }
                >
                  {!isMobile && <Perf style={{ width: 80, margin: '0 auto 8px' }} />}
                  <div
                    className="ts-num"
                    style={{
                      fontSize: isMobile ? 20 : 22,
                      fontWeight: 700,
                      color: 'var(--stamp)',
                    }}
                  >
                    ₹{formatNumber(balance.amount)}
                  </div>
                  {!isMobile && (
                    <>
                      <Perf style={{ width: 80, margin: '8px auto 6px' }} />
                      <button
                        className="ts-btn"
                        style={{ padding: '4px 10px', fontSize: 9 }}
                        onClick={() =>
                          setPending({
                            fromId: balance.from,
                            toId: balance.to,
                            amount: balance.amount,
                            fromName: sender,
                            toName: receiver,
                          })
                        }
                      >
                        Mark as settled
                      </button>
                    </>
                  )}
                </div>

                {/* Mobile-only settle button row */}
                {isMobile && (
                  <div style={{ gridArea: 'action', display: 'flex', justifyContent: 'center' }}>
                    <button
                      className="ts-btn"
                      style={{ padding: '6px 12px', fontSize: 10 }}
                      onClick={() =>
                        setPending({
                          fromId: balance.from,
                          toId: balance.to,
                          amount: balance.amount,
                          fromName: sender,
                          toName: receiver,
                        })
                      }
                    >
                      Mark as settled
                    </button>
                  </div>
                )}

                {/* Creditor */}
                <div
                  style={
                    isMobile
                      ? { gridArea: 'to', textAlign: 'right', minWidth: 0 }
                      : { textAlign: 'right' }
                  }
                >
                  <div className="ts-label">To · creditor</div>
                  <div
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: isMobile ? 17 : 22,
                      fontWeight: 500,
                      marginTop: 2,
                      color: 'var(--ledger)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {receiver}
                  </div>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div style={{ padding: '54px 24px', textAlign: 'center' }}>
            <div className="ts-label" style={{ marginBottom: 6 }}>
              All accounts square
            </div>
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                fontSize: 26,
                color: 'var(--ink-soft)',
              }}
            >
              No debts to declare.
            </div>
          </div>
        )}
      </div>

      <SelfExpenseDialog
        open={selfExpenseDialog}
        onClose={() => setSelfExpenseDialog(false)}
      />

      <ConfirmationDialog
        open={pending !== null}
        message={
          pending
            ? `Record that ${pending.fromName} paid ${pending.toName} ₹${formatNumber(
                pending.amount,
              )} to settle up?`
            : ''
        }
        onSubmit={recordSettlement}
        onCancel={() => setPending(null)}
      />
    </motion.div>
  );
};

export default BalanceContainer;
